import { Card } from '@components/card';
import { FormLabel } from '@components/form-label';
import { IconButton } from '@components/icon-button';
import { IconMerge } from '@components/icons/merge';
import { IconPlay } from '@components/icons/play';
import { IconRemove } from '@components/icons/remove';
import { Select } from '@components/select';
import { Separator } from '@components/separator';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';

import {
  useCreateJourneyActionRelationship,
  useJourneyAction,
  useJourneyActionRelationships,
  useJourneyActions,
  useRemoveJourneyActionRelationship,
} from 'api/journey-actions/hooks';
import { BASE_ACTION_TYPE } from 'api/types';
import {
  ActionHeader,
  ActionRemoveButton,
  ActionSidebarLoading,
  SidebarProps,
} from 'ui/journey-builder/node-types/common';

import { ActionIcon } from '../action-icon';
import { ActionLabel, actionLabels } from '../action-label';

type ActionType = 'journey_merge';

const SmallActionCard = ({
  action,
  description,
  shortId,
}: {
  action: BASE_ACTION_TYPE;
  description?: string;
  shortId?: string | null;
}) => {
  const removeMutation = useRemoveJourneyActionRelationship();
  return (
    <Card spaceX={4} spaceY={2}>
      <Stack orientation="horizontal" justify="space-between">
        <Stack space={2} orientation="horizontal" align="center">
          {action === 'journey_start' ? (
            <IconPlay color="green100" />
          ) : action === 'journey_merge' ? (
            <IconMerge color="orange100" />
          ) : (
            <ActionIcon action={action} />
          )}
          <div>
            <ActionLabel action={action} font="semibold14" />
            {description && <Text font="regular14" color="textSecondary" text={description} />}
          </div>
        </Stack>
        {shortId && (
          <IconButton
            onClick={() => removeMutation.mutateAsync(shortId)}
            icon={<IconRemove />}
            variant="ghost-purple"
          />
        )}
      </Stack>
    </Card>
  );
};

type RelationshipAction = {
  shortId: string | null;
  action: BASE_ACTION_TYPE;
  title?: string;
};

const findParentAction = (actions: any[], shortId: string | undefined): any | undefined => {
  if (!shortId) return undefined;

  const action = actions.find((a) => a.shortId === shortId);
  if (!action || !['journey_branch', 'branch_base'].includes(action.action)) return action;

  return findParentAction(actions, action.parentAction?.shortId);
};

export const ignoreTypes: BASE_ACTION_TYPE[] = [
  'journey_merge',
  'journey_start',
  'journey_branch',
  'branch_base',
  'journey_loop',
  'wait_for_time',
];

export const SidebarContent = (props: SidebarProps) => {
  const { journeyActionId } = props;
  const { data, isLoading } = useJourneyAction<ActionType>(journeyActionId);
  const { t } = useScopedTranslate(sidebarLocale);
  const journeyActionsQuery = useJourneyActions(data?.journey ?? '');
  const relationshipsQuery = useJourneyActionRelationships(journeyActionId);

  const addMutation = useCreateJourneyActionRelationship();

  if (!relationshipsQuery.data || !data || !journeyActionsQuery.data) {
    return (
      <ActionSidebarLoading title={t('title')} icon={IconMerge} iconColor="orange100" {...props} />
    );
  }

  const parentAction = findParentAction(journeyActionsQuery.data, data.parentAction?.shortId);

  if (!parentAction) {
    return (
      <ActionSidebarLoading title={t('title')} icon={IconMerge} iconColor="orange100" {...props} />
    );
  }

  const relationshipActions: RelationshipAction[] = [];

  const relationshipShortIds = relationshipsQuery.data.actions.map(
    (relationship) => relationship.parentAction
  );

  relationshipsQuery.data.actions.forEach((relationship) => {
    const action = journeyActionsQuery.data.find(
      (journeyAction) => journeyAction.shortId === relationship.parentAction
    );
    if (action) {
      relationshipActions.push({
        shortId: relationship.shortId,
        action: action.action,
        title: action.description,
      });
    }
  });

  const ancestors_short_ids = data.ancestors.map((ancestor) => ancestor[0]);

  const validMergeActions = journeyActionsQuery.data.filter(
    (journeyAction) =>
      !ignoreTypes.includes(journeyAction.action) &&
      journeyAction.shortId !== journeyActionId &&
      !ancestors_short_ids.includes(journeyAction.shortId) &&
      !data.descendants.includes(journeyAction.shortId) &&
      !relationshipShortIds.includes(journeyAction.shortId)
  );

  const handleSave = ({
    parentShortId,
    childShortId,
  }: {
    parentShortId: string;
    childShortId: string;
  }) => {
    return addMutation.mutateAsync({ parentShortId, childShortId });
  };
  const relationshipOptions = validMergeActions.map((action) => {
    return {
      value: action.shortId,
      helpText: action.description || t('noDescription'),
      label: (
        <Stack>
          <Stack space={2} orientation="horizontal" align="center">
            <ActionIcon action={action.action} />
            {actionLabels[action.action]}
          </Stack>
        </Stack>
      ),
    };
  });

  if (isLoading || !data) {
    return (
      <ActionSidebarLoading title={t('title')} icon={IconMerge} iconColor="orange100" {...props} />
    );
  }

  return (
    <Sidebar>
      <ActionHeader title={t('title')} icon={IconMerge} iconColor="orange100" {...props} />
      <Sidebar.Content>
        <Stack space={6}>
          <Stack space={4}>
            <FormLabel text={t('previous')} helpText={t('previousHelpText')} />
            <DirectLine />
            <SmallActionCard
              key={parentAction.shortId}
              action={parentAction.action}
              description={parentAction.description || t('noDescription')}
            />
            <Text text={t('previousNeeds')} font="regular14" />
          </Stack>

          <Stack space={4} orientation="horizontal" style={{ width: '100%' }} align="center">
            <Separator />
            <Text font="semibold14" color="textHelper" text={t('core.and')} />
            <Separator />
          </Stack>

          <Stack space={4}>
            {relationshipActions.length > 0 && (
              <>
                <Text text={t('selectedActions')} font="semibold14" />
                <RelationshipLine />
                <Stack space={2}>
                  {relationshipActions.map((action) => (
                    <SmallActionCard
                      key={action.shortId}
                      action={action.action}
                      description={action.title || t('noDescription')}
                      shortId={action.shortId}
                    />
                  ))}
                </Stack>
                <Text text={t('selectedActionsNeeds')} font="regular14" />
              </>
            )}

            <Select
              noOptions={() => t('noRelationships')}
              label={t('addRelationship')}
              options={relationshipOptions}
              onChange={(option) =>
                option && handleSave({ childShortId: journeyActionId, parentShortId: option })
              }
            />
          </Stack>
        </Stack>
      </Sidebar.Content>
      <Sidebar.Footer>
        <ActionRemoveButton action={data} />
      </Sidebar.Footer>
    </Sidebar>
  );
};

export function DirectLine() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="4" viewBox="0 0 428 4" fill="none">
      <path
        d="M426 2L2 2.00004"
        stroke={theme.color.purple20}
        strokeWidth="4"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function RelationshipLine() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="2" viewBox="0 0 426 2" fill="none">
      <path
        d="M425 1L1 1.00004"
        stroke={theme.color.orange100}
        strokeLinecap="round"
        strokeDasharray="16 16"
      />
    </svg>
  );
}

const sidebarLocale = {
  en: {
    noRelationships: 'No available actions',
    previous: 'Previous action',
    previousNeeds: 'Needs to be completed or manually skipped',
    previousHelpText: '(In workflow pipeline)',
    title: 'Wait for other actions',
    selectedActions: 'Selected actions',
    selectedActionsNeeds: 'Need to be completed or manually / conditionally skipped',
    addRelationship: 'Add action',
    noDescription: 'No description',
  },
};
